/**
 * Phreatic Site Modeling Page
 *
 * Unified dashboard: hydrograph + 3D view always visible at top,
 * tabs below for Map, Calculators (Sat Thickness, Volume, Depletion,
 * Cone of Depression, Sustainable Rate, Recharge, Water Quality).
 */
import React, { useState, useEffect, useCallback, Suspense, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ChartBarIcon,
  MapIcon,
  CubeIcon,
  ArrowPathIcon,
  BoltIcon,
  BeakerIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, Area, AreaChart, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, BarChart, Bar, ReferenceLine, Legend,
} from 'recharts';
import { FadeIn } from '../../components/animations/FadeIn';
import {
  phreaticApi,
  PhreaticPageBundle, WellPoint, WaterTableContour,
  AutoAssessResponse,
} from '../../api/phreatic';
import { apiClient } from '../../api/client';
import { WaterLevelChart } from '../../components/phreatic/WaterLevelChart';
import { WaterTableMap } from '../../components/phreatic/WaterTableMap';
import { WaterTable3DView } from '../../components/phreatic/WaterTable3DView';
import { PhreaticTimeSlider } from '../../components/phreatic/PhreaticTimeSlider';
// DrawdownTab replaced by ConeOfDepressionTab
import { WaterQualityPanel } from '../../components/phreatic/WaterQualityPanel';
import { GeologicalCrossSection } from '../../components/phreatic/GeologicalCrossSection';
import { ReportingPanel } from './ReportingPanel';
import { ReportView } from '../../components/phreatic/ReportView';
import { CavitationPanel } from '../../components/phreatic/CavitationPanel';
import { generateClientContours } from '../../components/phreatic/clientContours';
import { PumpOptimizerPanel } from '../../components/phreatic/PumpOptimizerPanel';
import { EconomicsPanel } from '../../components/phreatic/EconomicsPanel';
import { NewDevelopmentPanel } from '../../components/phreatic/NewDevelopmentPanel';
import { WellInstallationPanel } from '../../components/phreatic/WellInstallationPanel';
import { PumpControlPanel } from '../../components/phreatic/PumpControlPanel';
import { ConeOfDepressionTab } from '../../components/phreatic/ConeOfDepressionTab';
import { ManualDemandConfig } from '../../components/phreatic/ManualDemandConfig';
import { deriveWellGpm, manualDemandAfYr, afYrToGpm, type ManualDemand } from '@/lib/wellGpm';
import { WellInventoryPanel } from '../../components/phreatic/WellInventoryPanel';
import { SystemFlowDiagram } from '../../components/phreatic/SystemFlowDiagram';

const WELL_COLORS = [
  '#06b6d4', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16',
  '#e879f9', '#22d3ee',
];

// Single source of truth for a synthetic cluster's display name. Used for BOTH
// the hydrograph chain (clusterSeries) and the V6 prediction input (well_id), so
// the ensemble forecast links to the correct cluster series by exact-name match.
const clusterDisplayName = (c: any, i: number) =>
  `⬢ Cluster ${i + 1} (${c.aquifer}, ${c.well_count} wells)`;

// Error boundary for 3D
class View3DErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; errorMessage: string }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }
  static getDerivedStateFromError(error: Error) {
    return { hasError: true, errorMessage: error.message };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="h-[400px] bg-mono-950 rounded-lg flex items-center justify-center border border-mono-800">
          <p className="text-red-400 text-sm">{this.state.errorMessage}</p>
        </div>
      );
    }
    return this.props.children;
  }
}

type TabId = 'hydrograph' | 'well-table' | 'map' | 'cross-section' | 'volume' | 'economics' | 'new-development' | 'cone' | 'sustainable' | 'cavitation' | 'pump-schedule' | 'well-installation' | 'well-inventory' | 'system-flow' | 'recharge' | 'water-quality' | 'report';

const TAB_DEFS: Record<TabId, { label: string; desc: string }> = {
  'hydrograph':       { label: 'Hydrograph', desc: 'Multi-well water level time series with V5 predictions' },
  'well-table':       { label: '3D Well Table', desc: 'Interactive 3D water table surface with well markers' },
  'map':              { label: 'Well Map', desc: '2D satellite map with well markers and contours' },
  'cross-section':    { label: 'Cross-Section', desc: 'Geological vertical profile — ground surface, water table, aquifer base' },
  'volume':           { label: 'Volume', desc: 'Groundwater volume available (Fetter 2001: Vw = S x A x Δh)' },
  'economics':        { label: 'Economics', desc: 'Demand, valuation, cost analysis, crop trade-off — all in one' },
  'new-development':   { label: 'New Developments', desc: '' },
  'cone':             { label: 'Cone of Depression', desc: 'Single well + wellfield drawdown cross-sections + ROI map — driven by use case pump rate' },
  'sustainable':      { label: 'Sustainable Yield', desc: 'Max sustainable pump rate that holds saturated thickness above threshold for target lifespan' },
  'cavitation':       { label: 'Cavitation', desc: 'Per-well cavitation risk timeline (3mo→20yr) — early warning system' },
  'pump-schedule':    { label: 'Pump Control', desc: 'Interference-aware pump scheduling — manage well assets, generate optimal on/off cycles, track conflicts' },
  'well-installation':{ label: 'Well Intelligence', desc: 'AI-driven recommendations for WHERE to drill and HOW DEEP based on predicted water table decline' },
  'well-inventory':   { label: 'Well Inventory', desc: 'All site well assets — imported + AI recommended. CSV import, gauging entry, V5 bridge.' },
  'recharge':         { label: 'Recharge', desc: 'Precipitation & aquifer recharge vs extraction' },
  'water-quality':    { label: 'Quality', desc: 'Livestock, irrigation, drinking suitability' },
  'system-flow':      { label: 'Mermaid', desc: 'Full data flow architecture — ingestion, inference, calculators, downstream cascade' },
  'report':           { label: 'Reporting', desc: 'Generate, view, and download regulatory + lending + operational reports for this site' },
};

// Main sidebar tabs (top-level)
const MAIN_TABS: TabId[] = ['hydrograph', 'well-table', 'map', 'cross-section', 'volume', 'economics', 'new-development', 'report'];

// Well Operations group (collapsible dropdown)
const WELL_OPS_TABS: TabId[] = ['well-inventory', 'well-installation', 'pump-schedule', 'sustainable', 'cavitation', 'cone'];

// All tabs for iteration
const TABS = Object.entries(TAB_DEFS).map(([id, def]) => ({ id: id as TabId, ...def }));

export function PhreaticSiteModeling() {
  const { siteId } = useParams<{ siteId: string }>();

  // Page bundle state
  const [bundle, setBundle] = useState<PhreaticPageBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dateWells, setDateWells] = useState<WellPoint[] | null>(null);
  const [dateContours, setDateContours] = useState<WaterTableContour | null>(null);
  const [loadingDate, setLoadingDate] = useState(false);
  const [showPredictions, setShowPredictions] = useState(false);
  const [assetTimeseries, setAssetTimeseries] = useState<any[]>([]);
  const [generatingPredictions, setGeneratingPredictions] = useState(false);

  // V5 horizon predictions: per-well water elevations at 3mo/1yr/2yr/5yr/10yr/20yr
  const [predictionHorizons, setPredictionHorizons] = useState<Record<string, Record<string, number>> | null>(null); // {well_name: {3mo: elev, 1yr: elev, ...}}
  const [gaugingRecency, setGaugingRecency] = useState(5); // years — only predict wells gauged within this window

  // Calculator state
  const [viewMode, setViewMode] = useState<'solo' | 'dashboard'>('dashboard');
  const [soloTab, setSoloTab] = useState<TabId>('hydrograph');
  const [dashboardPanels, setDashboardPanels] = useState<Set<TabId>>(new Set(['hydrograph', 'well-table']));

  // Deep-link via URL hash (#report, #map, etc.) — used by CARI chat to
  // navigate the user to the Reporting tab after a generation completes.
  useEffect(() => {
    const applyHash = () => {
      const raw = window.location.hash.replace(/^#/, '').trim();
      if (!raw) return;
      if (raw in TAB_DEFS) {
        setViewMode('solo');
        setSoloTab(raw as TabId);
      }
    };
    applyHash();
    window.addEventListener('hashchange', applyHash);
    return () => window.removeEventListener('hashchange', applyHash);
  }, []);
  const [wellOpsOpen, setWellOpsOpen] = useState(true);
  const [gridCols, setGridCols] = useState<1 | 2 | 3>(2);
  const [calcData, setCalcData] = useState<AutoAssessResponse | null>(null);
  const [calcLoading, setCalcLoading] = useState(false);
  const [calcError, setCalcError] = useState<string | null>(null);
  const [licenseLocked, setLicenseLocked] = useState(false);  // 402 from auto-assess (no dashboard license)
  const [siteAcres, setSiteAcres] = useState(640);
  const [pumpHrs, setPumpHrs] = useState(12);
  const [targetYears, setTargetYears] = useState(20);
  const [measuredGpm, setMeasuredGpm] = useState<number | null>(null);
  // Site total GPM derived from each well's own DTW gauging (inverse Theis),
  // same shared logic the Cone of Depression tab uses. Null = no well had
  // usable gauging or a manual rate. Feeds the Sustainable Yield demand so
  // it reflects what the wells actually pull, not a hypothetical use case.
  const [derivedGpm, setDerivedGpm] = useState<number | null>(null);
  const [derivedWellCount, setDerivedWellCount] = useState(0);

  // Manual demand fallback (shared by Cone of Depression + Sustainable Yield).
  // Used only when there's no gauging-derived or manual-asset rate: the user
  // enters a use case + quantity + well count, split into a per-well GPM.
  const [manualDemand, setManualDemand] = useState<ManualDemand>({ useCase: '', quantity: 0, numWells: 1 });
  const manualTotalGpm = afYrToGpm(manualDemandAfYr(manualDemand));

  // Aquifer filter
  const [aquiferFilter, setAquiferFilter] = useState<string | null>(null);
  const [availableAquifers, setAvailableAquifers] = useState<{name: string; well_count: number}[]>([]);

  // Parcel management — each parcel has its own use case + scale
  interface Parcel {
    id: string;
    name: string;
    useCase: string;
    scale: number;
    acres?: number;
    bounds?: { north: number; south: number; east: number; west: number } | null;
  }
  const [parcels, setParcels] = useState<Parcel[]>([]);
  const [expandedParcel, setExpandedParcel] = useState<string | null>(null);

  function updateParcel(id: string, updates: Partial<Parcel>) {
    setParcels(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  }

  // Legacy single-parcel compat — use first parcel or empty
  const useCase = parcels.length > 0 ? parcels[0].useCase : '';
  const scale = parcels.length > 0 ? parcels[0].scale : 0;

  // Use-case config: determines labels, GPM calculation, and demand type
  // Use case config: gpmFormula = instantaneous GPM during irrigation/pumping season
  // annualAF = actual annual water consumption in acre-feet
  const USE_CASE_CONFIG: Record<string, { label: string; scaleLabel: string; scaleUnit: string; demandType: string; gpmFormula: (s: number) => number; annualAF: (s: number) => number; defaultScale: number }> = {
    cattle_feedyard: { label: 'Cattle Feedyard', scaleLabel: 'Head Count', scaleUnit: 'head', demandType: 'cattle', gpmFormula: (s) => s * 12 / 1440, annualAF: (s) => s * 12 * 365.25 / 325851, defaultScale: 15000 },
    dairy:           { label: 'Dairy', scaleLabel: 'Head Count', scaleUnit: 'head', demandType: 'cattle', gpmFormula: (s) => s * 35 / 1440, annualAF: (s) => s * 35 * 365.25 / 325851, defaultScale: 5000 },
    poultry:         { label: 'Poultry', scaleLabel: 'Head Count', scaleUnit: 'head', demandType: 'poultry', gpmFormula: (s) => s * 0.5 / 1440, annualAF: (s) => s * 0.5 * 365.25 / 325851, defaultScale: 500000 },
    corn:            { label: 'Corn Irrigation', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 2.0 / (1.614 * 0.5), annualAF: (s) => s * 2.0, defaultScale: 500 },
    sorghum:         { label: 'Sorghum Irrigation', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 1.1 / (1.614 * 0.5), annualAF: (s) => s * 1.1, defaultScale: 500 },
    cotton:          { label: 'Cotton Irrigation', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 1.5 / (1.614 * 0.5), annualAF: (s) => s * 1.5, defaultScale: 500 },
    alfalfa:         { label: 'Alfalfa Irrigation', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 3.5 / (1.614 * 0.5), annualAF: (s) => s * 3.5, defaultScale: 300 },
    wheat:           { label: 'Wheat Irrigation', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 0.8 / (1.614 * 0.5), annualAF: (s) => s * 0.8, defaultScale: 640 },
    peanuts:         { label: 'Peanuts', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 1.8 / (1.614 * 0.5), annualAF: (s) => s * 1.8, defaultScale: 500 },
    blackeyed_peas:  { label: 'Black-Eyed Peas', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 1.0 / (1.614 * 0.5), annualAF: (s) => s * 1.0, defaultScale: 500 },
    potatoes:        { label: 'Potatoes', scaleLabel: 'Irrigated Acres', scaleUnit: 'acres', demandType: 'custom', gpmFormula: (s) => s * 1.8 / (1.614 * 0.5), annualAF: (s) => s * 1.8, defaultScale: 500 },
    data_center:     { label: 'Data Center', scaleLabel: 'Cooling Capacity', scaleUnit: 'MW', demandType: 'data_center', gpmFormula: (s) => s * 500000 / 1440, annualAF: (s) => s * 500000 * 365.25 / 325851, defaultScale: 5 },
    municipal:       { label: 'Municipal', scaleLabel: 'Population', scaleUnit: 'people', demandType: 'municipal', gpmFormula: (s) => s * 100 / 1440, annualAF: (s) => s * 100 * 365.25 / 325851, defaultScale: 10000 },
    oil_gas:         { label: 'Oil & Gas (Frac)', scaleLabel: 'Wells/Year', scaleUnit: 'wells', demandType: 'custom', gpmFormula: (s) => s * 15.3 * 325851 / (365.25 * 1440), annualAF: (s) => s * 15.3, defaultScale: 20 },
  };

  // Per-parcel info helper
  const getParcelInfo = (p: Parcel) => USE_CASE_CONFIG[p.useCase] || { label: 'Select Use Case', scaleLabel: 'Scale', scaleUnit: 'units', demandType: 'custom', gpmFormula: () => 0, annualAF: () => 0, defaultScale: 0 };

  // Legacy single-parcel info (for components that need a single use case)
  const useCaseInfo = USE_CASE_CONFIG[useCase] || { label: 'Select Use Case', scaleLabel: 'Scale', scaleUnit: 'units', demandType: 'custom', gpmFormula: () => 0, annualAF: () => 0, defaultScale: 0 };

  // ── Master aggregation across all parcels ──
  const computedAnnualAF = Math.round(
    parcels.reduce((sum, p) => sum + (getParcelInfo(p).annualAF(p.scale) || 0), 0) * 10
  ) / 10;
  const computedDailyGal = Math.round(computedAnnualAF * 325851 / 365.25);
  const demandGpm24hr = parcels.reduce((sum, p) => sum + (getParcelInfo(p).gpmFormula(p.scale) || 0), 0);
  // Pump-rate precedence: explicit manual override > gauging-derived actual draw.
  // Use-case demand no longer feeds the dashboard — the ML decline already
  // embeds real pumping, so layering a use-case rate double-counts. Use case
  // now lives only in New Developments (what-if) and the Theis tabs' manual
  // fallback. No gauging/manual → 0 (panels that need a rate handle it).
  const computedGpm = measuredGpm != null && measuredGpm > 0
    ? measuredGpm
    : (derivedGpm != null && derivedGpm > 0)
      ? Math.round(derivedGpm * 10) / 10
      : (manualTotalGpm > 0 ? Math.round(manualTotalGpm * 10) / 10 : 0);
  // Where the pump rate came from (drives the Sustainable Yield label).
  const demandSource: 'manual' | 'derived' | 'manual_use_case' | 'none' =
    measuredGpm != null && measuredGpm > 0 ? 'manual'
    : derivedGpm != null && derivedGpm > 0 ? 'derived'
    : manualTotalGpm > 0 ? 'manual_use_case'
    : 'none';

  // Derive headCount and pumpGpm for backward compat with auto-assess
  const headCount = parcels.reduce((sum, p) => {
    if (['cattle_feedyard', 'dairy', 'poultry'].includes(p.useCase)) return sum + p.scale;
    return sum;
  }, 0);
  const pumpGpm = computedGpm / Math.max(bundle?.wells?.length || 10, 1);

  // Load page bundle + asset timeseries
  useEffect(() => {
    if (!siteId) return;
    loadBundle();
    loadAssetTimeseries();
  }, [siteId]);

  const [configSaved, setConfigSaved] = useState(false);
  const [declineSource, setDeclineSource] = useState<'historical' | 'inference'>('historical');

  async function loadBundle() {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await phreaticApi.getPageBundle(siteId);
      setBundle(data);
      if (data.site_area_acres) {
        setSiteAcres(Math.round(data.site_area_acres));
      }
      if (data.available_dates.length > 0) {
        const latest = data.available_dates[data.available_dates.length - 1];
        setSelectedDate(latest);
        setDateWells(data.wells);
        setDateContours(data.contours || generateClientContours(data.wells, latest));
      }
      // Load parcels + config
      let parcelsLoaded = false;
      try {
        const cfg = await apiClient.get(`/phreatic/sites/${siteId}/config`);
        if (cfg.data) {
          if (cfg.data.parcels && cfg.data.parcels.length > 0) {
            setParcels(cfg.data.parcels);
            parcelsLoaded = true;
          } else if (cfg.data.useCase) {
            setParcels([{
              id: 'parcel-legacy',
              name: 'Primary',
              useCase: cfg.data.useCase,
              scale: cfg.data.scale || 0,
            }]);
            parcelsLoaded = true;
          }
          if (cfg.data.pumpHrs) setPumpHrs(cfg.data.pumpHrs);
          if (cfg.data.targetYears) setTargetYears(cfg.data.targetYears);
          if (parcelsLoaded) setConfigSaved(true);
        }
      } catch { /* no saved config */ }

      // If no parcels from config, initialize from snip data
      if (!parcelsLoaded && data.property_bounds) {
        const snipParcels = (data.property_bounds as any)?.parcels;
        if (snipParcels && Array.isArray(snipParcels) && snipParcels.length > 0) {
          setParcels(snipParcels.map((sp: any, i: number) => ({
            id: `parcel-snip-${i}`,
            name: sp.name || `Parcel ${i + 1}`,
            useCase: '',
            scale: 0,
            acres: sp.acres || 0,
            bounds: sp.bounds || null,
          })));
        } else {
          setParcels([{
            id: 'parcel-primary',
            name: 'Primary',
            useCase: '',
            scale: 0,
          }]);
        }
      }
    } catch (err) {
      setError('Failed to load site data');
    } finally {
      setLoading(false);
    }
  }

  async function loadAssetTimeseries() {
    if (!siteId) return;
    try {
      const resp = await apiClient.get(`/phreatic/sites/${siteId}/asset-timeseries`);
      setAssetTimeseries(resp.data.timeseries || []);
    } catch { setAssetTimeseries([]); }
  }

  async function saveConfig() {
    if (!siteId) return;
    try {
      await apiClient.post(`/phreatic/sites/${siteId}/config`, {
        parcels, pumpHrs, targetYears, siteAcres,
        // Legacy compat — first parcel becomes the single use case
        useCase: parcels[0]?.useCase || '',
        scale: parcels[0]?.scale || 0,
        computedAnnualAF,
        savedAt: new Date().toISOString(),
      });
      setConfigSaved(true);
    } catch { /* ignore */ }
  }

  // Load V5 multi-horizon predictions (aquifer-routed LSTM + LGB + CatBoost ensemble)
  useEffect(() => {
    if (!siteId || !bundle) return;
    (async () => {
      try {
        setGeneratingPredictions(true);
        // Recency cutoff — only predict wells with gauging data within the window
        const recencyCutoff = gaugingRecency > 0
          ? new Date(Date.now() - gaugingRecency * 365.25 * 86400000).toISOString().slice(0, 10)
          : null;

        // Build prediction inputs from reference wells (bundle) + imported wells (assets)
        const allTimeseries = [...bundle.timeseries, ...assetTimeseries];
        const wellInputs = allTimeseries.map(ts => {
          // Reference wells: metadata lives on bundle.wells.
          // Imported PumpAssets: metadata is now flattened on the timeseries entry itself
          // (see /sites/{id}/asset-timeseries response shape).
          const wellPt = bundle.wells.find(w => w.well_name === ts.well_name);
          const groundElev = wellPt?.ground_elevation_ft ?? (ts as any).ground_elevation_ft ?? 0;
          const wellDepth = wellPt?.total_depth_ft ?? (ts as any).total_depth_ft ?? 300;
          const lat = wellPt?.latitude ?? (ts as any).latitude ?? 0;
          const lon = wellPt?.longitude ?? (ts as any).longitude ?? 0;
          const dtw = wellPt?.depth_to_water_ft ?? (ts as any).depth_to_water_ft ?? 0;

          if (!lat || !lon) return null;

          let validMeas = ts.measurements
            .filter((m: any) => m.water_elevation_ft != null && m.date);
          if (recencyCutoff) {
            validMeas = validMeas.filter((m: any) => m.date >= recencyCutoff);
          }
          validMeas = validMeas.slice(-20);
          if (validMeas.length < 3) return null;
          return {
            well_id: ts.well_name,
            measurements: validMeas.map((m: any, i: number, arr: any[]) => ({
              date: m.date,
              water_elevation_ft: m.water_elevation_ft!,
              depth_to_water_ft: m.depth_to_water_ft ?? 0,
              precip_mm: 0, temp_c: 20, et0_mm: 0,
              days_since_last: i > 0 ? Math.max(1, Math.round((new Date(m.date).getTime() - new Date(arr[i-1].date).getTime()) / 86400000)) : 90,
            })),
            well_depth_ft: wellDepth,
            ground_elevation_ft: groundElev,
            lat,
            lon,
            sat_thickness_ft: Math.max(0, wellDepth - dtw),
            aquifer_name: (wellPt as any)?.aquifer_name ?? 'other',
          };
        }).filter(Boolean);

        // Synthetic clusters → V6 inference. Now anchored to real ground elevation
        // (EPQS), each cluster's member series speaks the ensemble's datum, so it
        // forecasts like a well. Built directly here — NOT through the recency
        // filter above, because a cluster's multi-decade drill-date span IS its
        // signal — and named via clusterDisplayName so the forecast auto-draws on
        // the matching violet hydrograph chain.
        const clusterPredictInputs = (((bundle as any).synthetic_clusters || []) as any[])
          .map((c: any, i: number) => {
            const meas = (c.members || [])
              .filter((m: any) => m.water_elevation_ft != null && m.date)
              .map((m: any, idx: number, arr: any[]) => ({
                date: m.date,
                water_elevation_ft: m.water_elevation_ft,
                depth_to_water_ft: m.depth_to_water_ft ?? 0,
                precip_mm: 0, temp_c: 20, et0_mm: 0,
                days_since_last: idx > 0
                  ? Math.max(1, Math.round((new Date(m.date).getTime() - new Date(arr[idx - 1].date).getTime()) / 86400000))
                  : 90,
              }));
            if (meas.length < 3) return null;
            return {
              well_id: clusterDisplayName(c, i),
              measurements: meas,
              well_depth_ft: c.avg_well_depth_ft ?? 300,
              ground_elevation_ft: c.ground_ref_ft ?? 0,
              lat: c.lat,
              lon: c.lon,
              sat_thickness_ft: c.avg_sat_thickness_ft ?? Math.max(0, (c.avg_well_depth_ft ?? 300) - c.current_depth_to_water_ft),
              aquifer_name: c.aquifer ?? 'other',
            };
          }).filter(Boolean);

        const predictInputs = [...(wellInputs as any[]), ...(clusterPredictInputs as any[])];

        if (predictInputs.length > 0) {
          const BATCH_SIZE = 25;
          const horizonData: Record<string, Record<string, number>> = {};
          const totalBatches = Math.ceil(predictInputs.length / BATCH_SIZE);

          for (let b = 0; b < totalBatches; b++) {
            const batch = predictInputs.slice(b * BATCH_SIZE, (b + 1) * BATCH_SIZE);
            try {
              const resp = await apiClient.post('/phreatic/v4/predict/batch', {
                site_id: siteId,
                wells: batch,
              });
              for (const w of resp.data.wells || []) {
                horizonData[w.well_id] = {};
                for (const [hz, pred] of Object.entries(w.horizons || {})) {
                  horizonData[w.well_id][hz] = (pred as any).predicted_ft;
                }
              }
              // Update progressively — show predictions as they come in
              setPredictionHorizons({ ...horizonData });
              setShowPredictions(true);
            } catch (err) {
              console.warn(`Batch ${b + 1}/${totalBatches} failed:`, err);
            }
          }
          setPredictionHorizons({ ...horizonData });
          setShowPredictions(true);
        }
      } catch (err) {
        console.warn('V5 predictions failed:', err);
      } finally {
        setGeneratingPredictions(false);
      }
    })();
  }, [siteId, bundle, gaugingRecency]);

  // Run calculators
  const runCalculators = useCallback(async () => {
    if (!siteId) return;
    setCalcLoading(true);
    setCalcError(null);
    try {
      const result = await phreaticApi.autoAssess({
        site_id: siteId,
        site_area_acres: siteAcres,
        // No use-case demand on the dashboard — decline/years/availability are
        // purely empirical (V6 + gauging). Use-case modeling lives in New
        // Developments. Send zero demand so the backend returns the natural
        // (observed) depletion, not a double-counted use-case deduction.
        demand_type: 'custom' as any,
        head_count: 0,
        custom_gpd: 0,
        pump_rate_gpm_per_well: pumpGpm,
        pump_schedule_hrs: pumpHrs,
        target_years: targetYears,
        include_recharge: true,
        gauging_recency_years: gaugingRecency,
        aquifer_filter: aquiferFilter,
      });
      setCalcData(result);
      // Capture available aquifers from response
      console.log('[Phreatic] auto-assess response aquifers:', result.available_aquifers, 'active:', (result as any).active_aquifer);
      if (result.available_aquifers && result.available_aquifers.length > 0) {
        setAvailableAquifers(result.available_aquifers);
      }
    } catch (err: any) {
      if (err?.response?.status === 402) {
        setLicenseLocked(true);  // no dashboard license — show upgrade panel
      } else {
        const d = err?.response?.data?.detail;
        setCalcError(typeof d === 'string' ? d : 'Calculator failed');
      }
    } finally {
      setCalcLoading(false);
    }
  }, [siteId, siteAcres, useCase, scale, pumpHrs, targetYears, gaugingRecency, parcels, computedDailyGal, headCount, pumpGpm, aquiferFilter]);

  // Derive the site's actual draw from each well's DTW gauging (inverse Theis),
  // using the shared lib so this matches the Cone of Depression tab exactly.
  // Sums the per-well effective Q; feeds the Sustainable Yield demand via the
  // computedGpm precedence above. Re-runs when the detected aquifer changes
  // (T/S inputs to the inversion are per-aquifer).
  useEffect(() => {
    if (!siteId) return;
    const aquifer = calcData?.aquifer_detected ?? 'other';
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get(`/phreatic/pump-control/assets/${siteId}?status=active`);
        const assets: any[] = resp.data.assets || [];
        let total = 0;
        let contributing = 0;
        await Promise.all(
          assets.map(async (a: any) => {
            let gaugings: Array<{ date: string | null; depth_to_water_ft: number | null }> = [];
            try {
              const g = await apiClient.get(`/phreatic/pump-control/assets/${a.id}/gaugings`);
              gaugings = g.data.gaugings || [];
            } catch {
              // fall through to manual inside deriveWellGpm
            }
            const q = deriveWellGpm(a, gaugings, aquifer);
            if (q.gpm > 0) {
              total += q.gpm;
              contributing += 1;
            }
          }),
        );
        if (!cancelled) {
          setDerivedGpm(contributing > 0 ? total : null);
          setDerivedWellCount(contributing);
        }
      } catch {
        if (!cancelled) {
          setDerivedGpm(null);
          setDerivedWellCount(0);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [siteId, calcData?.aquifer_detected]);

  // Auto-run calculators once after bundle loads (siteAcres set in loadBundle before this fires)
  const hasAutoRun = useRef(false);
  useEffect(() => {
    if (siteId && bundle && !hasAutoRun.current) {
      // Runs even without use case — sat thickness, volume, Floyd Factor load immediately
      const t = setTimeout(() => { runCalculators(); hasAutoRun.current = true; }, 100);
      return () => clearTimeout(t);
    }
  }, [siteId, bundle]);

  // Re-run calculators when aquifer filter changes — call API directly with new value
  const prevAquifer = useRef<string | null>(null);
  useEffect(() => {
    if (aquiferFilter && aquiferFilter !== prevAquifer.current && hasAutoRun.current && siteId && bundle) {
      prevAquifer.current = aquiferFilter;
      setCalcLoading(true);
      setCalcError(null);
      phreaticApi.autoAssess({
        site_id: siteId,
        site_area_acres: siteAcres,
        // Empirical only — no use-case demand (see runCalculators).
        demand_type: 'custom' as any,
        head_count: 0,
        custom_gpd: 0,
        pump_rate_gpm_per_well: pumpGpm,
        pump_schedule_hrs: pumpHrs,
        target_years: targetYears,
        include_recharge: true,
        gauging_recency_years: gaugingRecency,
        aquifer_filter: aquiferFilter,
      }).then(result => {
        setCalcData(result);
        if (result.available_aquifers) setAvailableAquifers(result.available_aquifers);
      }).catch(err => {
        setCalcError(err?.response?.data?.detail || 'Calculator failed');
      }).finally(() => setCalcLoading(false));
    }
  }, [aquiferFilter]);

  // V5 horizon dates for the time slider (6 snap points in the future)
  const HORIZON_DAYS: Record<string, number> = { '3mo': 90, '1yr': 365, '2yr': 730, '5yr': 1825, '10yr': 3650, '20yr': 7300 };
  const horizonFutureDates = useMemo(() => {
    if (!predictionHorizons || Object.keys(predictionHorizons).length === 0) return [];
    const now = new Date();
    return Object.entries(HORIZON_DAYS).map(([label, days]: [string, number]) => {
      const d = new Date(now.getTime() + days * 86400000);
      return { date: d.toISOString().slice(0, 10), label, days };
    });
  }, [predictionHorizons]);

  const futureDates = horizonFutureDates.map(h => h.date);

  // Build synthetic WellPoints for each horizon date (for the 3D map)
  const horizonWellPoints = useMemo(() => {
    if (!predictionHorizons || !bundle) return new Map<string, WellPoint[]>();
    const map = new Map<string, WellPoint[]>();
    for (const { date, label } of horizonFutureDates as { date: string; label: string; days: number }[]) {
      const pts: WellPoint[] = [];
      for (const w of bundle.wells) {
        const horizonPreds = predictionHorizons[w.well_name];
        if (!horizonPreds || !horizonPreds[label]) continue;
        const predElev = horizonPreds[label];
        const ge = w.ground_elevation_ft ?? 0;
        const predDtw = ge > 0 ? ge - predElev : null;
        const pumpDepth = (w.total_depth_ft ?? 300) * 0.8;
        const cavitated = predDtw != null && predDtw >= pumpDepth;
        pts.push({
          ...w,
          water_elevation_ft: predElev,
          depth_to_water_ft: predDtw,
          gauge_date: date,
          is_dry: cavitated,  // 3D view colors dry wells red
        });
      }
      if (pts.length > 0) map.set(date, pts);
    }
    return map;
  }, [predictionHorizons, bundle, horizonFutureDates]);

  // Build chart predictions from V5 horizons — interpolated to quarterly points
  // so the 20yr future has the same visual density as the 20yr historical
  const chartPredictions = useMemo(() => {
    if (!predictionHorizons || !showPredictions) return undefined;
    const now = new Date();
    const STEP_MS = 90 * 86400000; // quarterly

    return Object.entries(predictionHorizons).map(([wellName, horizons]) => {
      const wellPt = bundle?.wells.find(w => w.well_name === wellName);
      const importedTs = (assetTimeseries || []).find((a: any) => (a.well_id ?? a.well_name) === wellName || a.well_name === wellName);
      // Synthetic clusters carry their anchored ground reference (EPQS) — match by
      // the shared display name so the forecast's depth-to-water resolves correctly.
      const clusterMatch = (((bundle as any)?.synthetic_clusters || []) as any[])
        .find((c: any, i: number) => clusterDisplayName(c, i) === wellName);
      const ge = wellPt?.ground_elevation_ft ?? (importedTs as any)?.ground_elevation_ft ?? clusterMatch?.ground_ref_ft ?? 0;

      // Build the 6 known V5 horizon points sorted by days
      const knownPoints: { days: number; elev: number }[] = [];
      for (const [label, days] of Object.entries(HORIZON_DAYS) as [string, number][]) {
        const elev = horizons[label];
        if (elev && elev > 0) knownPoints.push({ days, elev });
      }
      if (knownPoints.length < 2) return null;
      knownPoints.sort((a, b) => a.days - b.days);

      // Interpolate to quarterly steps (every 90 days from now to 20yr)
      const steps = [];
      for (let d = 90; d <= 7300; d += 90) {
        // Linear interpolation between bounding known points
        let elev: number;
        if (d <= knownPoints[0].days) {
          elev = knownPoints[0].elev;
        } else if (d >= knownPoints[knownPoints.length - 1].days) {
          elev = knownPoints[knownPoints.length - 1].elev;
        } else {
          // Find bounding points
          let lo = knownPoints[0], hi = knownPoints[knownPoints.length - 1];
          for (let i = 0; i < knownPoints.length - 1; i++) {
            if (d >= knownPoints[i].days && d <= knownPoints[i + 1].days) {
              lo = knownPoints[i];
              hi = knownPoints[i + 1];
              break;
            }
          }
          const t = (d - lo.days) / (hi.days - lo.days);
          elev = lo.elev + t * (hi.elev - lo.elev);
        }

        const targetDate = new Date(now.getTime() + d * 86400000).toISOString().slice(0, 10);
        // Widen CI as we go further out
        const yearsFwd = d / 365.25;
        const ciSpread = 3 + yearsFwd * 1.5;
        steps.push({
          target_date: targetDate,
          p10: elev - ciSpread,
          p50: elev,
          p90: elev + ciSpread,
          depth_to_water_ft: ge > 0 ? ge - elev : null,
          cavitation_risk: 0,
        });
      }
      return { well_name: wellName, steps };
    }).filter(Boolean) as { well_name: string; steps: any[] }[];
  }, [predictionHorizons, showPredictions, bundle, assetTimeseries]);

  // Convert imported PumpAsset timeseries to WellPoint shape so the
  // 3D map, 2D map, cross-section, cone, sustainable yield, and gradient
  // interpolation all see them alongside reference wells.
  // MUST be declared before any early returns to keep hook order stable.
  const importedWellPoints = useMemo(() => {
    return (assetTimeseries || [])
      .filter((a: any) => a.latitude != null && a.longitude != null)
      .map((a: any) => ({
        well_id: a.well_id ?? a.well_name,
        well_name: a.well_name,
        latitude: a.latitude,
        longitude: a.longitude,
        ground_elevation_ft: a.ground_elevation_ft ?? null,
        total_depth_ft: a.total_depth_ft ?? null,
        depth_to_water_ft: a.depth_to_water_ft ?? null,
        water_elevation_ft: a.water_elevation_ft ?? null,
        product_thickness_ft: null,
        gauge_date: a.gauge_date ?? null,
        is_dry: false,
        is_asset: true,
      })) as WellPoint[];
  }, [assetTimeseries]);

  // Synthetic clusters as hydrograph series (violet chains). Hydrograph + water
  // availability ONLY — never added to the 3D surface or gradient-map well arrays.
  const clusterSeries = useMemo(() => {
    const clusters = (bundle as any)?.synthetic_clusters || [];
    return clusters.map((c: any, i: number) => ({
      well_id: `cluster:${c.aquifer}:${i}`,
      well_name: clusterDisplayName(c, i),
      latitude: c.lat ?? null,
      longitude: c.lon ?? null,
      ground_elevation_ft: c.ground_ref_ft ?? null,
      total_depth_ft: null,
      screen_top_ft: null,
      screen_bottom_ft: null,
      aquifer_name: c.aquifer ?? 'other',
      measurements: (c.members || []).map((m: any) => ({
        date: m.date,
        depth_to_water_ft: m.depth_to_water_ft ?? null,
        water_elevation_ft: m.water_elevation_ft ?? null,
        product_thickness_ft: null,
        is_dry: false,
      })),
    }));
  }, [bundle]);

  // Median determinate cluster decline — feeds water availability where there's
  // no gauged-well depletion rate (areas covered only by drill-date wells).
  const clusterDecline = useMemo(() => {
    const cs = ((bundle as any)?.synthetic_clusters || [])
      .map((c: any) => c.decline_ft_yr)
      .filter((d: any) => typeof d === 'number')
      .sort((a: number, b: number) => a - b);
    return cs.length ? cs[Math.floor(cs.length / 2)] : null;
  }, [bundle]);

  const handleDateChange = useCallback(async (date: string) => {
    if (!siteId) return;
    setSelectedDate(date);

    // Future horizon date: use V5 predictions (no API call)
    const horizonPts = horizonWellPoints.get(date);
    if (horizonPts) {
      setDateWells(horizonPts);
      // Generate contours client-side from predicted water elevations
      const predictedContours = generateClientContours(horizonPts, date);
      setDateContours(predictedContours);
      return;
    }

    // Latest historical date: use bundle
    if (bundle && date === bundle.available_dates[bundle.available_dates.length - 1]) {
      setDateWells(bundle.wells);
      setDateContours(bundle.contours || generateClientContours(bundle.wells, date));
      return;
    }

    // Other historical date: fetch wells for that date, generate contours client-side
    setLoadingDate(true);
    try {
      const mapData = await phreaticApi.getWellMap(siteId, date);
      setDateWells(mapData.wells);
      // Try server contours first, fall back to client-generated
      try {
        const contourData = await phreaticApi.getWaterTableContours(siteId, date);
        setDateContours(contourData?.contours?.length ? contourData : generateClientContours(mapData.wells, date));
      } catch {
        setDateContours(generateClientContours(mapData.wells, date));
      }
    } catch (err) {
      console.error('Failed to load date data:', err);
    } finally {
      setLoadingDate(false);
    }
  }, [siteId, bundle, horizonWellPoints]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="w-6 h-6 text-mono-400 animate-spin" />
        <span className="ml-2 text-mono-400 text-sm">Loading groundwater data...</span>
      </div>
    );
  }

  if (licenseLocked) {
    return (
      <div className="p-6">
        <div className="max-w-lg mx-auto bg-mono-900 border border-cyan-500/30 rounded-lg p-8 text-center">
          <h2 className="text-lg font-semibold text-mono-100 mb-2">Dashboard Locked</h2>
          <p className="text-mono-400 text-sm mb-6">
            The full site dashboard requires a Dashboard License. Your plan includes
            CARI reports — upgrade to unlock the modeling dashboard for every site.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/phreatic/billing" className="px-4 py-2 text-sm rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30">
              Unlock Full Dashboard
            </Link>
            <Link to="/phreatic/sites" className="px-4 py-2 text-sm rounded border border-mono-700 text-mono-400 hover:text-mono-200">
              Back to Sites
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error || !bundle) {
    return (
      <div className="p-6">
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 text-red-400 text-sm">
          {error || 'No data available'}
          <button onClick={loadBundle} className="ml-3 underline hover:text-red-300">Retry</button>
        </div>
      </div>
    );
  }

  const currentWells = [...(dateWells || bundle.wells), ...importedWellPoints];
  // Aquifer-filtered wells for downstream tabs that should only see wells in the
  // currently-selected aquifer (Cavitation, Well Intelligence, Cross-Section, 2D Map).
  // null or '__combined__' = pass through everything.
  const aquiferWells = (() => {
    if (!aquiferFilter || aquiferFilter === '__combined__') return currentWells;
    const target = aquiferFilter.toLowerCase().trim();
    return currentWells.filter(w => (w.aquifer_name ?? '').toLowerCase().trim() === target);
  })();
  const currentContours = dateContours || bundle.contours;
  const s = calcData?.assessment?.summary;
  const vol = calcData?.assessment?.volume;
  const depl = calcData?.assessment?.depletion;
  const sust = calcData?.assessment?.sustainable_rate;
  const sat = calcData?.assessment?.saturated_thickness;
  const cone = calcData?.assessment?.cone_of_depression;
  const recharge = calcData?.recharge;

  // ── Panel layout helpers ──
  function togglePanel(id: TabId) {
    setDashboardPanels(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setViewMode('dashboard');
  }

  function goSolo(id: TabId) {
    setSoloTab(id);
    setViewMode('solo');
  }

  const shouldRender = (id: TabId) =>
    viewMode === 'solo' ? soloTab === id : dashboardPanels.has(id);

  const panelIds = viewMode === 'solo' ? [soloTab] : Array.from(dashboardPanels);

  // ── Render a single panel's content ──
  function renderPanel(id: TabId) {
    const needsCalc = !['map', 'cross-section', 'water-quality', 'well-installation', 'well-inventory', 'cavitation', 'pump-schedule', 'cone', 'hydrograph', 'well-table', 'system-flow'].includes(id);
    if (needsCalc && !calcData && !calcLoading) {
      return <div className="p-4 text-center text-mono-500 text-sm">Run Site Assessment to generate results</div>;
    }
    if (needsCalc && calcLoading && !calcData) {
      return <div className="p-4 text-center text-mono-400 text-sm animate-pulse">Running calculators...</div>;
    }

    switch (id) {
      case 'hydrograph':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-end gap-2">
              <span className="text-[10px] text-mono-500">Recency Filter</span>
              <select value={gaugingRecency} onChange={e => setGaugingRecency(Number(e.target.value))}
                className="px-1.5 py-0.5 text-[10px] bg-mono-800 border border-mono-700 rounded text-mono-300 focus:border-cyan-500 focus:outline-none"
                title="Only predict wells with gauging data within this window">
                <option value={2}>Last 2yr</option>
                <option value={5}>Last 5yr</option>
                <option value={10}>Last 10yr</option>
                <option value={20}>Last 20yr</option>
                <option value={0}>All time</option>
              </select>
            </div>
            <WaterLevelChart
              wells={[...bundle.timeseries, ...assetTimeseries, ...clusterSeries]}
              selectedDate={selectedDate}
              onDateSelect={handleDateChange}
              predictions={chartPredictions}
              showPredictions={showPredictions}
              aquiferFilter={aquiferFilter}
            />
          </div>
        );
      case 'well-table':
        return (
          <View3DErrorBoundary>
            <Suspense fallback={<div className="h-[350px] bg-mono-900 rounded-lg flex items-center justify-center"><ArrowPathIcon className="w-5 h-5 text-mono-400 animate-spin" /></div>}>
              <WaterTable3DView
                wells={currentWells}
                centerLat={bundle.latitude}
                centerLon={bundle.longitude}
                flowDirection={currentContours?.flow_direction_deg}
                selectedDate={selectedDate}
                siteAcres={siteAcres}
                propertyBounds={bundle.property_bounds}
                aquiferFilter={aquiferFilter}
              />
            </Suspense>
          </View3DErrorBoundary>
        );
      case 'map':
        return (
          <WaterTableMap centerLat={bundle.latitude} centerLon={bundle.longitude}
            wells={aquiferWells} contours={currentContours}
            flowDirection={currentContours?.flow_direction_deg}
            siteId={siteId} />
        );
      case 'cross-section':
        return (
          <GeologicalCrossSection
            wells={aquiferWells}
            centerLat={bundle.latitude}
            centerLon={bundle.longitude}
            aquifer={calcData?.aquifer_detected}
          />
        );
      case 'cone':
        return siteId ? (
          <ConeOfDepressionTab
            siteId={siteId}
            totalGpm={computedGpm}
            pumpHrs={pumpHrs}
            aquifer={calcData?.aquifer_detected ?? 'other'}
            centerLat={bundle.latitude}
            centerLon={bundle.longitude}
            manualDemand={manualDemand}
            onManualDemandChange={setManualDemand}
          />
        ) : null;
      case 'cavitation':
        return siteId ? (
          <CavitationPanel
            siteId={siteId}
            wells={aquiferWells.map(w => ({
              well_id: w.well_id, well_name: w.well_name,
              latitude: w.latitude, longitude: w.longitude,
              total_depth_ft: w.total_depth_ft, ground_elevation_ft: w.ground_elevation_ft,
              depth_to_water_ft: w.depth_to_water_ft, water_elevation_ft: w.water_elevation_ft,
            }))}
            timeseries={bundle.timeseries}
          />
        ) : null;
      case 'pump-schedule':
        return siteId ? (
          <PumpControlPanel
            siteId={siteId}
            aquifer={calcData?.aquifer_detected ?? 'other'}
            demandGalPerDay={Math.round(computedGpm * 1440)}
          />
        ) : null;
      case 'well-installation':
        return siteId ? (
          <WellInstallationPanel
            siteId={siteId}
            aquifer={calcData?.aquifer_detected ?? 'other'}
            wells={aquiferWells.map(w => ({
              well_name: w.well_name,
              latitude: w.latitude,
              longitude: w.longitude,
              total_depth_ft: w.total_depth_ft,
            }))}
            predictionHorizons={predictionHorizons}
            centerLat={bundle.latitude}
            centerLon={bundle.longitude}
            siteAcres={siteAcres}
            demandGpmPerWell={computedGpm / Math.max(aquiferWells.filter(w => w.depth_to_water_ft != null).length, 1)}
            propertyBounds={bundle.property_bounds}
          />
        ) : null;
      case 'well-inventory':
        return siteId ? (
          <WellInventoryPanel
            siteId={siteId}
            onWellsChanged={() => loadAssetTimeseries()}
            predictionHorizons={predictionHorizons}
            referenceWells={currentWells}
          />
        ) : null;
      case 'system-flow':
        return <SystemFlowDiagram />;
      case 'water-quality':
        return siteId ? <WaterQualityPanel siteId={siteId} /> : null;
      case 'report':
        return siteId ? (
          <div className="space-y-6">
            {/* Persistent library — every PDF that CARI or the user has generated */}
            <ReportingPanel siteId={siteId} />
            {/* Full groundwater availability assessment — printable */}
            <ReportView
              report={calcData?.report ?? null}
              loading={calcLoading}
              siteName={bundle.site_name}
              siteAcres={siteAcres}
              aquifer={calcData?.aquifer_detected ?? 'other'}
              wellCount={calcData?.wells_analyzed ?? 0}
              summary={s ?? undefined}
              volume={vol ?? undefined}
              satResults={sat ?? undefined}
              cone={cone ?? undefined}
              recharge={recharge ?? undefined}
              predictionHorizons={predictionHorizons ?? undefined}
            />
          </div>
        ) : null;
      case 'volume':
        if (!vol) return null;
        return renderVolumePanel();
      case 'economics':
        if (!vol || !s || !siteId) return null;
        return (
          <EconomicsPanel
            siteId={siteId}
            volumeAF={vol.volume_acre_feet}
            currentSatFt={s.avg_sat_thickness_ft}
            declineRate={(sat?.[0]?.depletion_rate_ft_per_year || clusterDecline) ?? 0}
            wellDepth={currentWells[0]?.total_depth_ft ?? 300}
            avgLiftFt={currentWells[0]?.depth_to_water_ft ?? 40}
            wellCount={currentWells.filter(w => w.depth_to_water_ft != null).length}
            aquifer={calcData?.aquifer_detected ?? 'other'}
            siteAcres={siteAcres}
            pumpGpm={pumpGpm}
            predictionHorizons={predictionHorizons}
            wells={currentWells}
            storativity={vol.storativity_used}
            initialUseCase={useCase}
            initialScale={scale}
          />
        );
      case 'new-development':
        if (!vol || !s || !siteId) return null;
        return (
          <NewDevelopmentPanel
            siteId={siteId}
            volumeAF={vol.volume_acre_feet}
            currentSatFt={s.avg_sat_thickness_ft}
            declineRate={(sat?.[0]?.depletion_rate_ft_per_year || clusterDecline) ?? 0}
            wellDepth={currentWells[0]?.total_depth_ft ?? 300}
            siteAcres={siteAcres}
            aquifer={calcData?.aquifer_detected ?? 'other'}
            initialUseCase={useCase}
          />
        );
      case 'sustainable':
        if (!sust) return null;
        return renderSustainablePanel();
      case 'recharge':
        if (!recharge) return null;
        return renderRechargePanel();
      default:
        return null;
    }
  }

  // ── Complex panel renderers (extracted from inline JSX) ──
  function renderVolumePanel() {
    if (!vol) return null;
    return (
          <div className="space-y-3">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">Volume Available</h3>
                <div className="space-y-4">
                  <div className="text-3xl font-bold text-cyan-400">{vol.volume_acre_feet.toLocaleString()} <span className="text-base text-mono-500">acre-feet</span></div>
                  <div className="text-mono-400 text-sm">{(vol.volume_gallons / 1e9).toFixed(2)} billion gallons &nbsp;|&nbsp; {vol.volume_ft3.toLocaleString()} ft³</div>

                  {/* Floyd Factor N/A — confined / consolidated aquifer */}
                  {calcData?.floyd_factor && calcData.floyd_factor.floyd_applicable === false && (
                    <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-2 text-[11px] text-mono-300">
                      <span className="text-blue-300 font-semibold">Floyd Factor N/A.</span>{' '}
                      {calcData.floyd_factor.geology?.aquifer} is a {calcData.floyd_factor.geology?.confinement} {calcData.floyd_factor.geology?.lithology} aquifer —
                      pressure/fracture-governed, not thickness-limited. The 40ft cutoff doesn't apply, so the full volume is recoverable.
                    </div>
                  )}

                  {/* Floyd Factor volume breakdown — only when Floyd applies */}
                  {calcData?.floyd_factor && calcData.floyd_factor.floyd_applicable !== false && (
                    <div className="bg-mono-800/50 rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3 rounded-sm bg-cyan-500/70" />
                            <span className="text-xs text-mono-300">Recoverable</span>
                          </div>
                          <div className="text-lg font-bold text-cyan-400">{calcData.floyd_factor.volume_economic_af.toLocaleString()} AF</div>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="inline-block w-3 h-3 rounded-sm bg-red-500/70" />
                            <span className="text-xs text-mono-300">Floyd Zone</span>
                          </div>
                          <div className="text-lg font-bold text-red-400">{calcData.floyd_factor.volume_floyd_zone_af.toLocaleString()} AF</div>
                        </div>
                      </div>
                      <div className="h-2 bg-mono-800 rounded-full overflow-hidden flex">
                        <div className="bg-cyan-500/60 h-full" style={{ width: `${(calcData.floyd_factor.volume_economic_af / Math.max(vol.volume_acre_feet, 1)) * 100}%` }} />
                        <div className="bg-red-500/60 h-full" style={{ width: `${(calcData.floyd_factor.volume_floyd_zone_af / Math.max(vol.volume_acre_feet, 1)) * 100}%` }} />
                      </div>
                    </div>
                  )}

                  {/* Years Remaining */}
                  {calcData?.floyd_factor?.site_floyd?.comparison && (
                    <div className="bg-mono-800/50 rounded-lg p-3 space-y-2">
                      <div className="text-[10px] text-mono-500 uppercase tracking-wider">Years Remaining</div>
                      {(() => {
                        const demandAF = calcData.floyd_factor.demand_af_per_year;
                        const rate = calcData.floyd_factor.decline_rates?.inference_ft_yr || 0;
                        const avgSat = s?.avg_sat_thickness_ft || 0;

                        // Natural decline: compute from displayed sat / V5 rate
                        const natLinear = rate > 0 ? Math.round(avgSat / rate * 10) / 10 : null;
                        const natFloyd = rate > 0 && avgSat > 40 ? Math.round((avgSat - 40) / rate * 10) / 10 : null;
                        // Use case demand: read directly from backend (same source as chart)
                        const demLinear = calcData.floyd_factor.demand_years_linear;
                        const demFloyd = calcData.floyd_factor.demand_years_floyd;

                        return (
                          <>
                            <div className="text-[9px] text-mono-500 mb-1">
                              V5 predicted decline: <span className="text-mono-300 font-semibold">{rate?.toFixed(2) ?? '—'} ft/yr</span>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-mono-400">Regional decline (existing use)</span>
                              <span>
                                <span className="text-mono-500">Linear: {natLinear ?? '—'} yr</span>
                                <span className="mx-2 text-mono-700">|</span>
                                <span className="text-cyan-400 font-semibold">Floyd: {natFloyd ?? '—'} yr</span>
                              </span>
                            </div>
                            {demandAF > 0 && (
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-mono-400">At use case ({demandAF.toLocaleString()} AF/yr)</span>
                                <span>
                                  <span className="text-mono-500">Linear: {demLinear ?? '—'} yr</span>
                                  <span className="mx-2 text-mono-700">|</span>
                                  <span className="text-red-400 font-semibold">Floyd: {demFloyd ?? '—'} yr</span>
                                </span>
                              </div>
                            )}
                          </>
                        );
                      })()}
                      <div className="text-[9px] text-mono-600 border-t border-mono-700 pt-2">
                        Floyd: bottom 40ft of aquifer is not economically recoverable. Linear = full volume. Floyd = recoverable volume only.
                      </div>
                    </div>
                  )}

                  {/* Per-parcel demand/years table removed — use case is no longer
                      configured on the dashboard, so there is no per-parcel demand
                      to break down. Empirical volume is shown in the cards above. */}

                  {/* Formula */}
                  <div className="bg-mono-800/50 rounded-lg p-3 space-y-1.5 font-mono text-xs text-mono-300">
                    <div className="text-[10px] text-mono-500 uppercase tracking-wider mb-1">Fetter (2001): V = S × A × Δh</div>
                    <div className="text-mono-400">
                      = {vol.storativity_used} × {siteAcres.toLocaleString()} ac × {s?.avg_sat_thickness_ft.toFixed(1)} ft = <span className="text-cyan-400">{vol.volume_acre_feet.toLocaleString()} AF</span>
                    </div>
                  </div>
                </div>
              </div>
              {vol.projected_volume.length > 0 && (
                <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">Projected Volume</h3>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={(() => {
                      const data = vol.projected_volume.filter((_: any, i: number) => i % 4 === 0);
                      if (!calcData?.floyd_factor) return data.map((d: any) => ({ ...d, recoverable: d.acre_feet_remaining, floyd_zone: 0 }));
                      // Compute Floyd zone per bar based on that bar's sat thickness proportion
                      // Volume in Floyd zone = volume × min(40 / sat_at_step, 1)
                      // sat_at_step comes from the projected sat thickness for that time step
                      const S = vol.storativity_used || 0.15;
                      const avgSatNow = s?.avg_sat_thickness_ft || 1;
                      const volPerFtSat = vol.volume_acre_feet / avgSatNow; // AF per ft of avg sat
                      return data.map((d: any, i: number) => {
                        const totalAF = d.acre_feet_remaining;
                        // Derive sat thickness from volume: sat = volume / volPerFtSat
                        const satAtStep = volPerFtSat > 0 ? totalAF / volPerFtSat : 0;
                        // Floyd zone = bottom 40ft worth of volume, or entire bar if sat < 40
                        const floydFt = Math.min(40, satAtStep);
                        const floydAF = floydFt * volPerFtSat;
                        const recoverableAF = Math.max(0, totalAF - floydAF);
                        return {
                          ...d,
                          recoverable: Math.round(recoverableAF),
                          floyd_zone: Math.round(floydAF),
                        };
                      });
                    })()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" tick={{ fill: '#888', fontSize: 10 }} />
                      <YAxis tick={{ fill: '#888', fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11 }} />
                      <Bar dataKey="floyd_zone" name="Floyd Zone (<40ft)" stackId="v" fill="#ef4444" fillOpacity={0.5} />
                      <Bar dataKey="recoverable" name="Recoverable" stackId="v" fill="#06b6d4" fillOpacity={0.7} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

          </div>
    );
  }

  function renderSustainablePanel() {
    if (!sust) return null;
    return (
      <div className="space-y-3">
        <ManualDemandConfig config={manualDemand} onChange={setManualDemand} />
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[10px] text-mono-500 uppercase tracking-wider">
                {demandSource === 'derived' ? 'Current Draw (from gauging)'
                  : demandSource === 'manual' ? 'Measured Demand'
                  : demandSource === 'manual_use_case' ? 'Manual Use-Case Demand'
                  : 'No Pumping Data'}
              </div>
              <div className="text-2xl font-bold text-cyan-400">{computedGpm} GPM</div>
              <div className="text-xs text-mono-500">
                {demandSource === 'derived'
                  ? `Derived from gauging across ${derivedWellCount} well${derivedWellCount === 1 ? '' : 's'}`
                  : demandSource === 'manual'
                  ? 'Manual override'
                  : demandSource === 'manual_use_case'
                  ? `Manual use-case demand across ${manualDemand.numWells} well${manualDemand.numWells === 1 ? '' : 's'}`
                  : 'Upload gauging data or set a manual rate'}
              </div>
            </div>
            <div className="text-3xl text-mono-700">vs</div>
            <div className="text-right">
              <div className="text-[10px] text-mono-500 uppercase tracking-wider">Max Sustainable ({targetYears}yr)</div>
              <div className={`text-2xl font-bold ${computedGpm <= sust.max_total_gpm ? 'text-cyan-400' : 'text-red-400'}`}>
                {sust.max_total_gpm} GPM
              </div>
              <div className="text-xs text-mono-500">
                {computedGpm <= sust.max_total_gpm
                  ? `${(sust.max_total_gpm - computedGpm).toFixed(0)} GPM headroom`
                  : `${(computedGpm - sust.max_total_gpm).toFixed(0)} GPM over capacity`}
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">Sustainable Pump Rate Scenarios</h3>
            {sust.scenarios.map(sc => {
              const pct = computedGpm > 0 ? Math.min(100, (computedGpm / sc.max_gpm) * 100) : 0;
              const over = computedGpm > sc.max_gpm;
              return (
                <div key={sc.years}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-mono-400">{sc.years}-year target</span>
                    <span className={over ? 'text-red-400' : 'text-cyan-400'}>
                      Max: {sc.max_gpm} GPM {over ? `(over by ${(computedGpm - sc.max_gpm).toFixed(0)})` : ''}
                    </span>
                  </div>
                  <div className="h-3 bg-mono-800 rounded-full overflow-hidden relative">
                    <div className={`h-full rounded-full ${over ? 'bg-red-500' : 'bg-cyan-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                    <div className="absolute top-0 h-full w-0.5 bg-white/50" style={{ left: `${Math.min(pct, 100)}%` }} />
                  </div>
                </div>
              );
            })}
            <div className="text-xs text-mono-500 pt-1 border-t border-mono-800">
              Demand: <span className="text-cyan-400">{computedGpm} GPM</span> |
              Per-well max ({targetYears}yr): <span className="text-mono-300">{sust.max_per_well_gpm} GPM</span> |
              Max cattle: <span className="text-mono-300">{sust.headcount_equivalents.cattle_at_12gal.toLocaleString()} head</span>
            </div>
          </div>
          {(() => {
            const hasRate = computedGpm > 0;
            const demandSustainable = hasRate && computedGpm <= sust.max_total_gpm;
            const borderCls = !hasRate ? 'border-mono-800' : demandSustainable ? 'border-cyan-500/30' : 'border-red-500/30';
            return (
          <div className={`bg-mono-900 border rounded-lg p-4 ${borderCls}`}>
            <div className="flex items-start gap-2">
              {!hasRate ? <BeakerIcon className="w-5 h-5 text-mono-500 mt-0.5" /> : demandSustainable ? <CheckCircleIcon className="w-5 h-5 text-cyan-400 mt-0.5" /> : <ExclamationTriangleIcon className="w-5 h-5 text-red-400 mt-0.5" />}
              <div>
                <div className={`text-sm font-semibold ${!hasRate ? 'text-mono-300' : demandSustainable ? 'text-cyan-300' : 'text-red-300'}`}>
                  {!hasRate
                    ? `No pumping rate yet — upload gauging data or set a manual rate to test sustainability against the ${targetYears}-year max.`
                    : demandSustainable
                    ? `Current draw of ${computedGpm} GPM is sustainable for ${targetYears} years`
                    : `Current draw of ${computedGpm} GPM exceeds the sustainable rate of ${sust.max_total_gpm} GPM for ${targetYears} years`}
                </div>
                <div className="mt-3 bg-mono-800/50 rounded p-2 text-[10px] text-mono-500 font-mono">
                  <div>max_vol = S × A × (sat × drawdown%)</div>
                  <div>= {vol?.storativity_used} × {(siteAcres * 43560).toLocaleString()} × ({s?.avg_sat_thickness_ft.toFixed(0)} × {sust.acceptable_drawdown_pct})</div>
                  <div>max_annual = max_vol / {targetYears}yr → max_gpm = {sust.max_total_gpm} GPM</div>
                </div>
              </div>
            </div>
          </div>
            );
          })()}
        </div>
      </div>
    );
  }

  function renderRechargePanel() {
    if (!recharge) return null;
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {recharge.annual_precip_history.length > 0 && (
          <div className="bg-mono-900 border border-mono-800 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider mb-3">
              Annual Precipitation — avg {recharge.average_annual_inches?.toFixed(1)}" / recharge {recharge.estimated_recharge_af_yr} AF/yr
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={recharge.annual_precip_history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis dataKey="year" tick={{ fill: '#888', fontSize: 10 }} />
                <YAxis tick={{ fill: '#888', fontSize: 10 }} />
                <Tooltip contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #444', fontSize: 11 }} />
                {recharge.average_annual_inches && <ReferenceLine y={recharge.average_annual_inches} stroke="#f59e0b" strokeDasharray="4 4" />}
                <Bar dataKey="precip_inches" fill="#60a5fa" fillOpacity={0.7} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">Recharge Details</h3>
          <div className="text-mono-300 text-sm">Aquifer: {recharge.aquifer}</div>
          <div className="text-mono-300 text-sm">Recharge rate: {recharge.recharge_rate_inches_yr} in/yr</div>
          <div className="text-mono-300 text-sm">Estimated recharge: {recharge.estimated_recharge_af_yr} AF/yr</div>
          {recharge.current_year_to_date_inches != null && (
            <div className="text-mono-300 text-sm">Current YTD precip: {recharge.current_year_to_date_inches}"</div>
          )}
        </div>
      </div>
    );
  }

  // ── Main Layout: Sidebar + Content ──
  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* ── Panel Sidebar ── */}
      <aside className="w-48 flex-shrink-0 bg-mono-950 border-r border-mono-800 overflow-y-auto">
        <div className="p-2">
          <div className="px-2 mb-2 space-y-1.5">
            <span className="text-[10px] text-mono-500 uppercase tracking-wider">Panels</span>
            <div className="flex items-center gap-1 bg-mono-900 rounded p-0.5">
              {([1, 2, 3] as const).map(n => (
                <button key={n} onClick={() => { setGridCols(n); setViewMode('dashboard'); }}
                  className={`flex-1 text-[9px] py-0.5 rounded transition-colors ${
                    gridCols === n && viewMode === 'dashboard' ? 'bg-cyan-500/20 text-cyan-300' : 'text-mono-500 hover:text-mono-300'
                  }`}
                  title={`${n} per row`}
                >
                  {n === 1 ? '\u2630' : n === 2 ? '\u2587\u2587' : '\u2587\u2587\u2587'}
                </button>
              ))}
            </div>
          </div>
          {MAIN_TABS.map(id => {
            const tab = TAB_DEFS[id];
            const isSolo = viewMode === 'solo' && soloTab === id;
            const isChecked = dashboardPanels.has(id);
            return (
              <SidebarTab key={id} id={id} label={tab.label} desc={tab.desc}
                isSolo={isSolo} isChecked={isChecked} viewMode={viewMode}
                onToggle={() => togglePanel(id)} onSolo={() => goSolo(id)} />
            );
          })}

          {/* Well Operations group */}
          <div className="mt-2 border-t border-mono-800 pt-2">
            <button
              onClick={() => setWellOpsOpen(!wellOpsOpen)}
              className="flex items-center gap-1 px-2 py-1 w-full text-left text-[10px] text-mono-400 uppercase tracking-wider hover:text-mono-200 transition-colors"
            >
              <span className={`text-[8px] transition-transform ${wellOpsOpen ? 'rotate-90' : ''}`}>&#9654;</span>
              Well Operations
            </button>
            {wellOpsOpen && WELL_OPS_TABS.map(id => {
              const tab = TAB_DEFS[id];
              const isSolo = viewMode === 'solo' && soloTab === id;
              const isChecked = dashboardPanels.has(id);
              return (
                <SidebarTab key={id} id={id} label={tab.label} desc={tab.desc}
                  isSolo={isSolo} isChecked={isChecked} viewMode={viewMode}
                  onToggle={() => togglePanel(id)} onSolo={() => goSolo(id)} indent />
              );
            })}
          </div>
        </div>
      </aside>

      {/* ── Main Content ── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {/* ── Header ── */}
        <div className="flex items-center gap-3">
          <Link to="/phreatic/dashboard" className="p-1.5 rounded-lg bg-mono-800 hover:bg-mono-700">
            <ArrowLeftIcon className="w-4 h-4 text-mono-400" />
          </Link>
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-mono-100">{bundle.site_name}</h1>
            <p className="text-mono-500 text-xs">
              {bundle.timeseries.length} wells | {bundle.available_dates.length} events
              {calcData && <> | {(calcData as any).active_aquifer || calcData.aquifer_detected} aquifer</>}
              {parcels.length > 0 && computedAnnualAF > 0 && (
                <span className="ml-2 text-cyan-400">
                  | {parcels.length} parcel{parcels.length !== 1 ? 's' : ''} | {computedAnnualAF.toLocaleString()} AF/yr | {computedGpm} GPM | {pumpHrs}hr/day
                </span>
              )}
            </p>
          </div>
          <div className="ml-auto flex items-center gap-2 flex-shrink-0">
            {loadingDate && <ArrowPathIcon className="w-4 h-4 text-cyan-400 animate-spin" />}
            {generatingPredictions && (
              <span className="flex items-center gap-1 text-[10px] text-cyan-300">
                <ArrowPathIcon className="w-3 h-3 animate-spin" /> Generating 20yr predictions...
              </span>
            )}
            {predictionHorizons && showPredictions && (
              <span className="text-[10px] text-cyan-400">
                V5 predictions loaded ({Object.keys(predictionHorizons).length} wells, 6 horizons)
              </span>
            )}
          </div>
        </div>

        {/* ── Aquifer Toggle (visible when 2+ aquifers) ── */}
        {availableAquifers.length >= 2 && (
          <div className="flex items-center gap-2 bg-mono-900 border border-mono-800 rounded-lg px-3 py-2">
            <span className="text-[10px] text-mono-500 uppercase tracking-wider mr-1">Aquifer</span>
            {availableAquifers.map(aq => {
              const isActive = aquiferFilter === aq.name;
              return (
                <button
                  key={aq.name}
                  onClick={() => { setAquiferFilter(aq.name); }}
                  className={`px-2.5 py-1 text-[10px] rounded border transition-all ${
                    isActive
                      ? 'border-cyan-500/60 bg-cyan-500/15 text-cyan-300'
                      : 'border-mono-700 bg-mono-800/50 text-mono-400 hover:border-mono-500'
                  }`}
                >
                  {aq.name} <span className="text-mono-600 ml-1">({aq.well_count})</span>
                </button>
              );
            })}
            <button
              onClick={() => { setAquiferFilter('__combined__'); }}
              className={`px-2.5 py-1 text-[10px] rounded border transition-all ${
                aquiferFilter === '__combined__'
                  ? 'border-cyan-500/60 bg-cyan-500/15 text-cyan-300'
                  : 'border-mono-700 bg-mono-800/50 text-mono-400 hover:border-mono-500'
              }`}
            >
              Combined <span className="text-mono-600 ml-1">({availableAquifers.reduce((s, a) => s + a.well_count, 0)})</span>
            </button>
          </div>
        )}

        {/* ── Summary Cards ── */}
        {s && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <MetricCard label="Avg Sat Thickness" value={`${s.avg_sat_thickness_ft.toFixed(0)} ft`}
              color={s.avg_sat_thickness_ft < 30 ? 'red' : s.avg_sat_thickness_ft < 80 ? 'amber' : 'cyan'} />
            <MetricCard label="Volume Available"
              value={`${(calcData?.floyd_factor?.volume_economic_af || vol?.volume_acre_feet || 0).toLocaleString()} AF`}
              color="cyan" />
            {(() => {
              // Empirical years remaining — Floyd-adjusted natural decline from
              // the gauging-trained V6 inference. No use-case demand layered on.
              const floydYrs = calcData?.floyd_factor?.adjusted_summary?.years_remaining_floyd;
              if (floydYrs != null) {
                // Confined/consolidated aquifers don't have a Floyd zone — drop
                // the "(Floyd)" qualifier so the label matches the geology.
                const floydNA = calcData?.floyd_factor?.floyd_applicable === false;
                return (
                  <MetricCard label={floydNA ? 'Years Remaining' : 'Years Remaining (Floyd)'}
                    value={`${floydYrs} yr`}
                    color={floydYrs < 10 ? 'red' : floydYrs < 30 ? 'amber' : 'green'} />
                );
              }
              // Fallback: linear estimate from the observed V5/V6 decline rate
              const avgDecline = sat && sat.length > 0
                ? sat.reduce((sum, w) => sum + Math.abs(w.depletion_rate_ft_per_year), 0) / sat.length
                : 0;
              const S = vol?.storativity_used || 0.15;
              const totalLossAF = avgDecline * S * siteAcres;
              const yrsFromDecline = totalLossAF > 0 ? (vol?.volume_acre_feet || 0) / totalLossAF : null;
              return (
                <MetricCard label="Years at Current Rate"
                  value={yrsFromDecline != null ? `${yrsFromDecline.toFixed(0)} yr` : 'Stable'}
                  color={yrsFromDecline != null ? (yrsFromDecline < 10 ? 'red' : yrsFromDecline < 30 ? 'amber' : 'green') : 'green'} />
              );
            })()}
            <MetricCard label="Decline Rate (V5)"
              value={calcData?.floyd_factor?.decline_rates?.inference_ft_yr != null ? `${calcData.floyd_factor.decline_rates.inference_ft_yr.toFixed(2)} ft/yr` : (sat && sat.length > 0 ? `${(sat.reduce((sum, w) => sum + w.depletion_rate_ft_per_year, 0) / sat.length).toFixed(1)} ft/yr` : '—')}
              color={calcData?.floyd_factor?.decline_rates?.inference_ft_yr != null ? (calcData.floyd_factor.decline_rates.inference_ft_yr > 2 ? 'red' : 'mono') : 'mono'} />
            <MetricCard label="Wells Analyzed" value={`${calcData?.wells_analyzed || 0}`} color="mono" />
          </div>
        )}

        {/* ── Time Slider ── */}
        <PhreaticTimeSlider
          dates={bundle.available_dates}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          futureDates={futureDates}
          futureLabels={Object.fromEntries(horizonFutureDates.map((h: { date: string; label: string }) => [h.date, h.label]))}
        />

        {/* ── Parcel Management / Site Assessment ── */}
        <div className="bg-mono-900 border border-mono-800 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-mono-400 uppercase tracking-wider">
              <BeakerIcon className="w-3.5 h-3.5 inline mr-1" />
              Site Assessment
            </h3>
            <div className="flex gap-2">
              <button onClick={() => { runCalculators(); saveConfig(); }} disabled={calcLoading}
                className="flex items-center gap-1 px-3 py-1 text-xs rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 hover:bg-cyan-500/30 disabled:opacity-50">
                {calcLoading ? <ArrowPathIcon className="w-3 h-3 animate-spin" /> : <BoltIcon className="w-3 h-3" />}
                {calcLoading ? 'Running...' : 'Run & Save'}
              </button>
            </div>
          </div>

          {/* Global settings */}
          <div className="flex items-center gap-3">
            <InputField label="Site Area (acres)" value={siteAcres} onChange={setSiteAcres} disabled={!!bundle?.site_area_acres} />
            <InputField label="Pump hrs/day" value={pumpHrs} onChange={setPumpHrs} min={1} max={24} />
            <InputField label="Target Years" value={targetYears} onChange={setTargetYears} min={5} max={100} />
            <div>
              <label className="text-[10px] text-mono-500 uppercase tracking-wider">Measured GPM (override)</label>
              <input type="number" value={measuredGpm ?? ''} onChange={e => {
                const v = e.target.value;
                setMeasuredGpm(v === '' ? null : Number(v));
              }} min={0} placeholder="auto"
                className="w-full mt-0.5 px-2 py-1 text-xs border rounded focus:outline-none bg-mono-800 border-mono-700 text-mono-200 focus:border-cyan-500" />
            </div>
            <div className="bg-mono-800/50 rounded p-1.5 min-w-[140px]">
              <div className="text-[9px] text-mono-500 uppercase">Pumping Rate</div>
              <div className="text-xs text-cyan-400 font-semibold">{computedGpm.toLocaleString()} GPM</div>
              <div className="text-[9px] text-mono-400">
                {demandSource === 'manual' ? 'measured override'
                  : demandSource === 'derived' ? 'gauging-derived'
                  : 'awaiting gauging'}
              </div>
            </div>
          </div>

          {/* Parcels — snipped property boundaries (informational; use case is
              configured in New Developments, not here). */}
          {parcels.length === 0 ? (
            <div className="text-center py-3 text-mono-500 text-xs">
              No parcels configured. Snip property boundaries in the Command Center to define parcels.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {parcels.map(parcel => (
                <div key={parcel.id} className="flex items-center gap-2 bg-mono-800/30 border border-mono-800 rounded px-3 py-1.5">
                  <input
                    type="text" value={parcel.name}
                    onChange={e => updateParcel(parcel.id, { name: e.target.value })}
                    className="bg-transparent text-xs text-mono-200 font-medium border-none focus:outline-none focus:text-cyan-300 w-28"
                  />
                  {parcel.acres ? <span className="text-[10px] text-mono-500">{parcel.acres.toLocaleString()} ac</span> : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {calcError && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-2 text-red-400 text-xs">{calcError}</div>
        )}

        {/* ── Panel Grid ── */}
        <div className={viewMode === 'dashboard'
          ? `grid gap-3 ${gridCols === 1 ? 'grid-cols-1' : gridCols === 2 ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`
          : ''
        }>
          {panelIds.map(id => {
            const tab = TAB_DEFS[id];
            const content = renderPanel(id);
            if (!content) return null;

            if (viewMode === 'solo') {
              return (
                <div key={id}>
                  <div className="text-[10px] text-mono-500 mb-2">{tab?.desc}</div>
                  {content}
                </div>
              );
            }

            return (
              <div key={id} className="bg-mono-900/50 border border-mono-800 rounded-lg overflow-hidden">
                <div className="px-3 py-1.5 border-b border-mono-800 flex items-center justify-between bg-mono-950/80">
                  <span className="text-[11px] font-semibold text-mono-400 uppercase tracking-wider">{tab.label}</span>
                  <div className="flex items-center gap-1.5">
                    <button onClick={() => goSolo(id)}
                      className="text-mono-600 hover:text-cyan-400 text-[10px]" title="Expand">
                      &#x2922;
                    </button>
                    <button onClick={() => togglePanel(id)}
                      className="text-mono-600 hover:text-red-400 text-[10px]" title="Close">
                      &#x2715;
                    </button>
                  </div>
                </div>
                <div className="p-2">{content}</div>
              </div>
            );
          })}
        </div>

      </div>
    </div>
  );
}

// ── Helper Components ──────────────────────────────────────────────────

function SidebarTab({ id, label, desc, isSolo, isChecked, viewMode, onToggle, onSolo, indent }: {
  id: string; label: string; desc: string;
  isSolo: boolean; isChecked: boolean; viewMode: string;
  onToggle: () => void; onSolo: () => void; indent?: boolean;
}) {
  return (
    <div
      className={`flex items-center gap-2 py-1.5 rounded text-[11px] cursor-pointer group transition-colors ${indent ? 'pl-5 pr-2' : 'px-2'} ${
        isSolo ? 'bg-cyan-500/10 text-cyan-300' : isChecked && viewMode === 'dashboard' ? 'bg-mono-800/50 text-mono-200' : 'text-mono-500 hover:text-mono-300 hover:bg-mono-900'
      }`}
    >
      <button
        onClick={e => { e.stopPropagation(); onToggle(); }}
        className={`w-3 h-3 rounded-full flex-shrink-0 border transition-colors ${
          isChecked
            ? 'bg-cyan-500 border-cyan-400'
            : 'bg-mono-700 border-mono-600 group-hover:border-mono-500'
        }`}
      />
      <span onClick={onSolo} className="flex-1 truncate leading-tight" title={desc}>
        {label}
      </span>
    </div>
  );
}

function InputField({ label, value, onChange, min, max, disabled }: {
  label: string; value: number; onChange: (v: number) => void; min?: number; max?: number; disabled?: boolean;
}) {
  return (
    <div>
      <label className="text-[10px] text-mono-500 uppercase tracking-wider">{label}</label>
      <input type="number" value={value} onChange={e => onChange(Number(e.target.value))} min={min} max={max} disabled={disabled}
        className={`w-full mt-0.5 px-2 py-1 text-xs border rounded focus:outline-none ${
          disabled
            ? 'bg-mono-850 border-mono-750 text-cyan-400 cursor-not-allowed'
            : 'bg-mono-800 border-mono-700 text-mono-200 focus:border-cyan-500'
        }`} />
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  const cls: Record<string, string> = {
    cyan: 'text-cyan-400 border-cyan-500/30',
    green: 'text-cyan-400 border-cyan-500/30',
    amber: 'text-cyan-400 border-cyan-500/30',
    red: 'text-red-400 border-red-500/30',
    mono: 'text-mono-300 border-mono-700',
  };
  const c = cls[color] || cls.mono;
  return (
    <div className={`bg-mono-900 border rounded-lg p-2 ${c.split(' ')[1]}`}>
      <div className="text-[9px] text-mono-500 uppercase tracking-wider">{label}</div>
      <div className={`text-sm font-bold mt-0.5 ${c.split(' ')[0]}`}>{value}</div>
    </div>
  );
}

function formatShortDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

